import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import {
  X, Check, RotateCcw, Download, History, ShoppingCart, Package,
  Truck, CheckCircle2, AlertTriangle, Plus, Search, Loader2, Building2,
  ClipboardList, Receipt, ArrowRight, BadgeCheck, Clock, DollarSign,
  TrendingUp, Banknote, ChevronDown, ChevronRight, Eye, BarChart2,
  MapPin, Filter, Calendar, AlertCircle, Boxes, MessageSquare,
  Printer, FileCheck, Pencil, Paperclip, UploadCloud, List, BookOpen,
  Tag, ExternalLink, Brain, Sparkles, FileUp, CheckCheck, Zap, EyeOff
} from "lucide-react";
import { formatDate, formatPYG } from "../../utils/format";
import { useCerrarConAtras } from "../../utils/useCerrarConAtras";
import { STATUS_LABELS, STATUS_COLORS, FLOW_STEPS, URGENCY_LABELS } from "./constants";
import { formatDateTime, buildHistory, generatePedidoPDF } from "./helpers";

// Fix ED (2026-09-24): "Cuando un pedido de compra pasa para aprobacion, ahi arriba
// necesitamos que salga el presupuesto de referencia junto con su codigo y el cliente".
export const presupuestoRef = (req, pto) => ({
  codigo: pto?.number || req?.presupuesto_codigo || req?.pto_objetivo || "",
  nombre: pto?.nombre || "",
  cliente: pto?.cliente || req?.lista_cliente_nombre || "",
});
export const textoPresupuestoRef = (ref) => [ref.codigo, ref.nombre].filter(Boolean).join(" — ") || "—";

export function Modal({ open, onClose, title, children, wide, extraWide, fullWidth, "data-testid": dataTestid }) {
  // BLOQUE 33 PARTE B — defensa extra: al bloquear/liberar el scroll de fondo
  // (necesario para que el modal no se desplace junto con el fondo) guardamos y
  // restauramos la posición exacta de scroll del contenedor scrolleable real de
  // la app (`<main class="overflow-y-auto">` en Layout.jsx — el layout NO usa
  // scroll de `window`), para que un reflow del fondo mientras el modal está
  // abierto/cerrándose nunca se note como un salto de scroll al usuario.
  const scrollTopRef = useRef(0);
  useEffect(() => {
    if (!open) return;
    const scrollEl = document.querySelector("main");
    scrollTopRef.current = scrollEl ? scrollEl.scrollTop : 0;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
      const el = document.querySelector("main");
      if (el) el.scrollTop = scrollTopRef.current;
    };
  }, [open, onClose]);
  // Fix AI (2026-09-04): con este modal abierto, el botón "atrás" del navegador
  // lo cierra y deja al usuario donde estaba (antes salía de la página).
  useCerrarConAtras(open, onClose);
  if (!open) return null;
  // Fix S (2026-09-01): `fullWidth` (max-w-7xl) — opt-in, no afecta los modales
  // existentes con `extraWide`/`wide`. Lo necesita el modal de Pedido de
  // Materiales, cuya grilla de ítems (min-w-[1180px], columna nueva "Ítem del
  // Pto") no entraba en max-w-5xl y cortaba Marca/Observación (hallazgo del
  // testing_agent).
  const maxW = fullWidth ? "max-w-7xl" : extraWide ? "max-w-5xl" : wide ? "max-w-3xl" : "max-w-lg";
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid={dataTestid}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${maxW} max-h-[92vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// BLOQUE 18 PARTE A — modal reutilizable "Seleccionar ítems para el PDF" (PP y PPS).
// Filtro EXCLUSIVO de exportación: no persiste nada en el documento real, solo decide
// qué ítems entran en el PDF que se está generando en ese momento.
export function SelectItemsForPdfModal({ open, onClose, items, itemLabel, onConfirm, title, description, providerFieldLabel, itemNoun, itemNounSingular }) {
  const [checked, setChecked] = useState({});
  const [providerName, setProviderName] = useState("");

  useEffect(() => {
    if (!open) return;
    const init = {};
    (items || []).forEach((_, i) => { init[i] = true; });
    setChecked(init);
    setProviderName("");
  }, [open, items]);

  if (!open) return null;
  const list = items || [];
  const selectedCount = Object.values(checked).filter(Boolean).length;
  const toggleOne = (i) => setChecked(prev => ({ ...prev, [i]: !prev[i] }));
  const toggleAll = (val) => {
    const next = {};
    list.forEach((_, i) => { next[i] = val; });
    setChecked(next);
  };
  const handleConfirm = () => {
    const selectedIndexes = list.map((_, i) => i).filter(i => checked[i]);
    if (selectedIndexes.length === 0) { return; }
    onConfirm(selectedIndexes, providerName.trim());
  };

  return (
    <Modal open={open} onClose={onClose} title={title || "Seleccionar ítems para el PDF"} wide>
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          {description || "Destildá los ítems que NO querés incluir en este PDF (por ejemplo, para enviar la cotización solo a un proveedor específico). Esta selección solo afecta al PDF generado ahora — el pedido guardado conserva TODOS sus ítems."}
        </p>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">{providerFieldLabel || "Proveedor destinatario (opcional)"}</label>
          <input
            type="text"
            value={providerName}
            onChange={e => setProviderName(e.target.value)}
            placeholder="Ej: Electropar, Vimar…"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none"
            data-testid="pdf-select-provider-name-input"
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{selectedCount} de {list.length} {itemNoun || "ítems"} seleccionados</span>
          <div className="flex gap-2">
            <button onClick={() => toggleAll(true)} className="text-xs font-semibold text-[#2E6DA4] hover:underline" data-testid="pdf-select-all-btn">Marcar todos</button>
            <button onClick={() => toggleAll(false)} className="text-xs font-semibold text-gray-500 hover:underline" data-testid="pdf-select-none-btn">Desmarcar todos</button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
          {list.map((it, i) => (
            <label key={i} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked[i] ? "bg-white" : "bg-gray-50 opacity-60"}`}>
              <input
                type="checkbox"
                checked={!!checked[i]}
                onChange={() => toggleOne(i)}
                className="w-4 h-4 rounded border-gray-300 text-[#2E6DA4] focus:ring-[#2E6DA4]/30"
                data-testid={`pdf-item-checkbox-${i}`}
              />
              <span className="text-xs font-medium text-gray-800 flex-1">{itemLabel(it)}</span>
            </label>
          ))}
        </div>
        {selectedCount === 0 && (
          <p className="text-xs text-red-500 font-medium">Seleccioná al menos un {itemNounSingular || "ítem"} para generar el PDF.</p>
        )}
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
          <button
            onClick={handleConfirm}
            disabled={selectedCount === 0}
            className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="pdf-select-confirm-btn">
            <Download size={14} /> Generar PDF
          </button>
        </div>
      </div>
    </Modal>
  );
}

// BLOQUE 25 PARTE A — "Opciones de impresión del PDF" del Acta Comparativa (AP):
// a diferencia de SelectItemsForPdfModal (una sola lista), aquí hay DOS secciones
// independientes — ítems (filas) Y proveedores (columnas) — ambas con checkboxes
// tildados por defecto (comportamiento actual = todo incluido). Filtro EXCLUSIVO de
// exportación: el AP en base de datos NUNCA se modifica.
export function SelectAPPdfOptionsModal({ open, onClose, act, onConfirm }) {
  const [itemsChecked, setItemsChecked] = useState({});
  const [provsChecked, setProvsChecked] = useState({});

  const items = act?.analysis_items || [];
  const providers = act?.provider_columns || [];

  useEffect(() => {
    if (!open) return;
    const ic = {}; items.forEach((_, i) => { ic[i] = true; });
    const pc = {}; providers.forEach((_, i) => { pc[i] = true; });
    setItemsChecked(ic);
    setProvsChecked(pc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, act]);

  if (!open) return null;
  const selItemsCount = Object.values(itemsChecked).filter(Boolean).length;
  const selProvsCount = Object.values(provsChecked).filter(Boolean).length;
  const toggleItem = (i) => setItemsChecked(prev => ({ ...prev, [i]: !prev[i] }));
  const toggleProv = (i) => setProvsChecked(prev => ({ ...prev, [i]: !prev[i] }));
  const toggleAllItems = (val) => { const n = {}; items.forEach((_, i) => { n[i] = val; }); setItemsChecked(n); };
  const toggleAllProvs = (val) => { const n = {}; providers.forEach((_, i) => { n[i] = val; }); setProvsChecked(n); };
  const itemsInvalid = items.length > 0 && selItemsCount === 0;
  const provsInvalid = providers.length > 0 && selProvsCount === 0;

  const handleConfirm = () => {
    if (itemsInvalid || provsInvalid) return;
    const visibleItemIdxs = items.map((_, i) => i).filter(i => itemsChecked[i]);
    const visibleProviderIdxs = providers.map((_, i) => i).filter(i => provsChecked[i]);
    onConfirm({ visibleItemIdxs, visibleProviderIdxs });
  };

  return (
    <Modal open={open} onClose={onClose} title="Opciones de impresión del PDF" extraWide>
      <div className="space-y-5">
        <p className="text-xs text-gray-500">
          Elegí qué ítems y qué proveedores incluir en este PDF comparativo — por ejemplo, para
          negociar con un proveedor mostrándole solo los ítems de su interés. Por defecto está
          todo tildado (comportamiento idéntico al actual). El análisis guardado en el sistema
          conserva SIEMPRE todos sus ítems y proveedores.
        </p>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Ítems a incluir ({selItemsCount} de {items.length})
            </span>
            <div className="flex gap-2">
              <button onClick={() => toggleAllItems(true)} className="text-xs font-semibold text-[#2E6DA4] hover:underline" data-testid="ap-pdf-items-select-all">Marcar todos</button>
              <button onClick={() => toggleAllItems(false)} className="text-xs font-semibold text-gray-500 hover:underline" data-testid="ap-pdf-items-select-none">Desmarcar todos</button>
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
            {items.map((it, i) => (
              <label key={i} className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${itemsChecked[i] ? "bg-white" : "bg-gray-50 opacity-60"}`}>
                <input
                  type="checkbox"
                  checked={!!itemsChecked[i]}
                  onChange={() => toggleItem(i)}
                  className="w-4 h-4 rounded border-gray-300 text-[#2E6DA4] focus:ring-[#2E6DA4]/30"
                  data-testid={`ap-pdf-item-checkbox-${i}`}
                />
                <span className="text-xs font-medium text-gray-800 flex-1">{it.item_name}</span>
              </label>
            ))}
          </div>
          {itemsInvalid && <p className="text-xs text-red-500 font-medium mt-1">Seleccioná al menos un ítem.</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Proveedores a incluir ({selProvsCount} de {providers.length})
            </span>
            <div className="flex gap-2">
              <button onClick={() => toggleAllProvs(true)} className="text-xs font-semibold text-[#2E6DA4] hover:underline" data-testid="ap-pdf-provs-select-all">Marcar todos</button>
              <button onClick={() => toggleAllProvs(false)} className="text-xs font-semibold text-gray-500 hover:underline" data-testid="ap-pdf-provs-select-none">Desmarcar todos</button>
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100">
            {providers.map((p, i) => (
              <label key={i} className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${provsChecked[i] ? "bg-white" : "bg-gray-50 opacity-60"}`}>
                <input
                  type="checkbox"
                  checked={!!provsChecked[i]}
                  onChange={() => toggleProv(i)}
                  className="w-4 h-4 rounded border-gray-300 text-[#2E6DA4] focus:ring-[#2E6DA4]/30"
                  data-testid={`ap-pdf-prov-checkbox-${i}`}
                />
                <span className="text-xs font-medium text-gray-800 flex-1">{p}</span>
              </label>
            ))}
          </div>
          {provsInvalid && <p className="text-xs text-red-500 font-medium mt-1">Seleccioná al menos un proveedor.</p>}
        </div>

        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
          <button
            onClick={handleConfirm}
            disabled={itemsInvalid || provsInvalid}
            className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2"
            data-testid="ap-pdf-options-confirm-btn">
            <Download size={14} /> Generar PDF
          </button>
        </div>
      </div>
    </Modal>
  );
}



export function StatCard({ title, value, icon: Icon, color = "blue", sub, testId }) {
  const colors = {
    blue:   "bg-blue-50 text-[#2E6DA4] border-blue-100",
    emerald:"bg-emerald-50 text-emerald-600 border-emerald-100",
    amber:  "bg-amber-50 text-amber-600 border-amber-100",
    red:    "bg-red-50 text-red-600 border-red-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <div className={`p-2 rounded-lg border ${colors[color]}`}><Icon size={16} /></div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || (status || "").replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full font-semibold border capitalize ${STATUS_COLORS[status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
      {label}
    </span>
  );
}

export function StatusTimeline({ currentStatus }) {
  const stepIndex = FLOW_STEPS.findIndex(s => s.key === currentStatus);
  const isRejected = currentStatus === "rechazada";
  const isModify = currentStatus === "modificar";
  // Fix S (2026-09-01): un borrador todavía no entró al flujo — mostrarlo en
  // FLOW_STEPS confundiría (parecería "pendiente" atrasado, cuando en realidad
  // nunca se envió). Caso propio, igual que rechazada/modificar.
  const isBorrador = currentStatus === "borrador";

  if (isBorrador) {
    return (
      <div className="flex items-center gap-2 py-1">
        <div className="w-5 h-5 rounded-full bg-slate-400 flex items-center justify-center"><Pencil size={10} className="text-white" /></div>
        <span className="text-xs font-semibold text-slate-600">Borrador — todavía sin confirmar</span>
      </div>
    );
  }
  if (isRejected) {
    return (
      <div className="flex items-center gap-2 py-1">
        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center"><X size={10} className="text-white" /></div>
        <span className="text-xs font-semibold text-red-600">Pedido Rechazado</span>
      </div>
    );
  }
  if (isModify) {
    return (
      <div className="flex items-center gap-2 py-1">
        <div className="w-5 h-5 rounded-full bg-orange-400 flex items-center justify-center"><RotateCcw size={10} className="text-white" /></div>
        <span className="text-xs font-semibold text-orange-600">Devuelto para modificación</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5 overflow-x-auto py-1 scrollbar-hide">
      {FLOW_STEPS.map((step, i) => {
        const isDone = i < stepIndex;
        const isCurrent = i === stepIndex;
        return (
          <div key={step.key} className="flex items-center flex-shrink-0">
            <div className="flex flex-col items-center min-w-[44px]">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold transition-all ${
                  isDone ? "bg-emerald-500 text-white" :
                  isCurrent ? "text-white shadow-md" : "bg-gray-200 text-gray-400"
                }`}
                style={isCurrent ? { backgroundColor: step.color } : {}}
              >
                {isDone ? <Check size={9} /> : i + 1}
              </div>
              <span className={`text-[8px] text-center mt-0.5 leading-tight max-w-[40px] ${isCurrent ? "font-bold" : "text-gray-400"}`}
                style={isCurrent ? { color: step.color } : {}}>
                {step.short}
              </span>
            </div>
            {i < FLOW_STEPS.length - 1 && (
              <div className={`h-px w-3 mb-3 flex-shrink-0 ${isDone ? "bg-emerald-400" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// CEN 2.0 FASE 3 PARTE B — etiquetas de categoría del ítem del PM (medición de consumo)
// Fix CW (2026-09-14): faltaban "materiales" (material de obra sin presupuesto
// vinculado), "epis" y "alquileres": las líneas de esas categorías mostraban un
// guion en la columna Categ. del detalle del pedido.
const PM_CATEGORIA_LABELS = { listado: "Del presupuesto", materiales: "Material", menores: "Material menor", imprevistos: "Imprevisto", equipamientos: "Equipamiento (Adm)", herramientas: "Herramienta (Adm)", epis: "EPI (Adm)", alquileres: "Alquiler (Adm)" };   // Fix EI
const CONSUMO_ESTADO_CLS = { excedido: "text-red-600", por_agotarse: "text-amber-600", ok: "text-emerald-600" };
// Fix CW (2026-09-14): mismo redondeo que el formulario de carga, para que el
// análisis no muestre 56.142857142857146 donde el residente vio 56.14.
const fmtCant = (v) => Math.round((Number(v) || 0) * 100) / 100;

// ── Fix DC (2026-09-16): "tampoco está haciendo bien el análisis" (Edson, 16/09) ──
// De los tres análisis solo salía el del medio. Faltaba el PRIMERO, el del material en
// todo el presupuesto, que es el que manda.
//
// La causa: el pedido guarda en `material_name` la ETIQUETA del desplegable, que ya trae
// la unidad entre paréntesis —"CABLE MULTIF 4,00 mm2 750V (MTS)"— y además normaliza la
// unidad de la fila a "m". La búsqueda volvía a pegarle la unidad, así que preguntaba por
// "cable multif 4,00 mm2 750v (mts) (m)" contra una tabla que tiene
// "cable multif 4,00 mm2 750v (mts)". No coincidía nunca, y el análisis por material no
// aparecía en ninguna línea elegida del desplegable, que son casi todas.
//
// Ahora se busca por DESCRIPCIÓN, sin mayúsculas, sin acentos y sin la unidad entre
// paréntesis, y la unidad solo se usa para desempatar si dos materiales se llaman igual.
// Si el empate no se puede resolver no se muestra nada, antes que mostrar el número de
// otro material.
const _normTxt = (s) => String(s == null ? "" : s).toLowerCase()
  .normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim();
// "CABLE MULTIF 4,00 mm2 750V (MTS)" -> "cable multif 4,00 mm2 750v"
const _sinUM = (s) => _normTxt(String(s == null ? "" : s).replace(/\s*\([^()]*\)\s*$/, ""));
const _UM_EQUIV = {
  m: "m", mt: "m", mts: "m", ml: "m", mtl: "m", metro: "m", metros: "m",
  m2: "m2", "m²": "m2", m3: "m3", "m³": "m3", kg: "kg", kgs: "kg",
  un: "un", u: "un", ud: "un", uds: "un", und: "un", unid: "un", unidad: "un", unidades: "un",
  pza: "un", pzas: "un", "c/u": "un", gl: "gl", glb: "gl", gbl: "gl", global: "gl",
  rollo: "rollo", rollos: "rollo", rll: "rollo", caja: "caja", cajas: "caja", cja: "caja",
  par: "par", pares: "par", juego: "juego", juegos: "juego", jgo: "juego", jgos: "juego",
};
const _normUM = (u) => { const k = _normTxt(u).replace(/\.$/, ""); return _UM_EQUIV[k] || k; };

// Índice de los materiales del presupuesto: por la descripción tal cual y por la
// descripción sin la unidad del final. Cada clave guarda TODAS las filas que caen ahí,
// para poder desempatar por unidad.
function _indexarMateriales(materiales) {
  const full = new Map(), base = new Map();
  (materiales || []).forEach(row => {
    if (!row || row.tipo !== "material") return;
    [[full, _normTxt(row.descripcion)], [base, _sinUM(row.descripcion)]].forEach(([mapa, clave]) => {
      if (!clave) return;
      if (!mapa.has(clave)) mapa.set(clave, []);
      if (!mapa.get(clave).includes(row)) mapa.get(clave).push(row);
    });
  });
  return { full, base };
}

function _buscarMaterial(idx, item) {
  if (!idx || !item) return null;
  const nombre = item.material_name;
  const filas = idx.full?.get(_normTxt(nombre)) || idx.base?.get(_sinUM(nombre));
  if (!filas || filas.length === 0) return null;
  if (filas.length === 1) return filas[0];
  const u = _normUM(item.unit);
  return filas.find(r => _normUM(r.unidad) === u) || null;
}

// Fix DJ (2026-09-19): "El analisis del pedido de un material que se imputa a
// varios items puede salir en una fila nomas? para realizar la compra, si esto
// llega asi a compras es demasiado complicado". Desde el Fix DG una fila
// imputada a varios ítems se guarda como una línea por ítem — bien para medir el
// consumo, ilegible para comprar: seis filas de la misma caja con 66,6667 cada
// una. Acá se juntan de nuevo para mostrarlas: una fila por material con la
// cantidad total, y adentro a qué ítem va cada parte. El dato guardado no cambia.
//
// Se agrupan solo las líneas que son el MISMO material en todo sentido: misma
// descripción, unidad, categoría, sector, frente y marca. Si difieren en algo de
// eso son pedidos distintos aunque se llamen igual, y se muestran separadas.
export const _claveAgrupacion = (it) => [
  String(it?.material_name || "").trim().toLowerCase(),
  String(it?.unit || "").trim().toLowerCase(),
  String(it?.categoria || "").trim().toLowerCase(),
  String(it?.sector || "").trim().toLowerCase(),
  String(it?.front || "").trim().toLowerCase(),
  String(it?.marca_recomendada || "").trim().toLowerCase(),
].join("|");

// Fix DK (2026-09-21): el análisis de una sola imputación — la parte de ESTE pedido
// que va a un ítem del presupuesto, contra lo que ese ítem tiene proyectado.
// Si la línea es material menor y hay montos, se muestra el fondo en guaraníes:
// "Materiales menores" en el presupuesto es un fondo de dinero, no una cantidad.
// Sin montos (Residente), queda el análisis por cantidad de siempre.
// Fix DN (2026-09-21): doble aprobación. Fix EX (2026-10-05): los perfiles que aprueban se
// configuran en Compras → Rango p/ Aprobaciones; el backend guarda en `_requeridos` cuáles
// tenía que aprobar este pedido. Sin esa lista (aprobados viejos), los dos lados del Fix DN.
const LADOS_APROBACION = [["coord", "Coordinador de Obras"], ["gerente", "Gerente Técnico"]];
const requeridosDe = (p) => (Array.isArray(p?._requeridos) && p._requeridos.length ? p._requeridos : LADOS_APROBACION);
export const mensajeAprobacion = (data, numero) => {
  if (!data || data.status === "aprobada") return numero ? `Pedido ${numero} aprobado` : "Pedido aprobado";
  const p = data.aprobaciones_parciales || {};
  const req = requeridosDe(p);
  const faltan = req.filter(([k]) => !p[k]).map(([, n]) => n);
  return `Aprobado ${req.length - faltan.length} de ${req.length}${numero ? ` (${numero})` : ""}. Falta el aprobado de ${faltan.join(" y ") || "otro perfil"}.`;
};
export function AprobacionParcial({ parciales, testId }) {
  const p = parciales || {};
  const req = requeridosDe(p);
  if (!req.some(([k]) => p[k])) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={testId || "aprobacion-parcial"}>
      {req.map(([k, n]) => p[k] ? (
        <span key={k} className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
          ✓ {n}: {p[k].by_name}
        </span>
      ) : (
        <span key={k} className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
          Falta: {n}
        </span>
      ))}
    </div>
  );
}

export function analisisImputacion({ it, consumo, ip, idx }) {
  if (!consumo) {
    return <span className="text-gray-400 text-[10px]" data-testid={`pedido-detalle-analisis-sin-datos-${idx}`}>sin datos del Pto</span>;
  }
  const etiqueta = ip ? `${ip.numero ? ip.numero + " " : ""}` : "";
  const esMenor = String(it?.categoria || "") === "menores";
  if (esMenor && consumo.monto_presupuestado != null) {
    const disponible = (Number(consumo.monto_presupuestado) || 0) - (Number(consumo.monto_comprado) || 0);
    return (
      <span className={`text-[10px] ${disponible < 0 ? "text-red-600 font-semibold" : "text-gray-600"}`} data-testid={`pedido-detalle-fondo-${idx}`}>
        {etiqueta}Fondo: {formatPYG(consumo.monto_presupuestado)} · usado {formatPYG(consumo.monto_comprado)} · <strong>disponible {formatPYG(disponible)}</strong>
      </span>
    );
  }
  return (
    <span className="text-[10px] text-gray-600" data-testid={`pedido-detalle-imputacion-analisis-${idx}`}>
      {etiqueta}pide {fmtCant(it?.quantity)} de {fmtCant(consumo.presupuestado)} · comprado {fmtCant(consumo.comprado)} ({consumo.pct_comprado}%)
    </span>
  );
}

export const agruparLineas = (items) => {
  const grupos = [];
  const porClave = new Map();
  (items || []).forEach((it, idx) => {
    const k = _claveAgrupacion(it);
    let g = porClave.get(k);
    if (!g) {
      g = { clave: k, base: it, idx, lineas: [], cantidad: 0 };
      porClave.set(k, g);
      grupos.push(g);
    }
    g.lineas.push({ it, idx });
    g.cantidad = Math.round((g.cantidad + (Number(it?.quantity) || 0)) * 10000) / 10000;
  });
  return grupos;
};

// Fix ED (2026-09-24): "cuando le damos descargar pdf que descargue con el analisis…
// necesitamos la info completa en el pdf tambien". Las mismas columnas del detalle
// (ítem del presupuesto, nivel y análisis), en texto.
const _gs = (v) => `Gs. ${Math.round(Number(v) || 0).toLocaleString("es-PY")}`;
const _txtMaterial = (cm) => `Material en el Pto: ${fmtCant(cm.presupuestado)} ${cm.unidad || ""} · pedido ${fmtCant(cm.solicitado)} · comprado ${fmtCant(cm.comprado)} (${cm.pct_comprado}%)`;
const _txtImputacion = (it, consumo, ip) => {
  if (!consumo) return "sin datos del Pto";
  const etiqueta = ip?.numero ? `${ip.numero} ` : "";
  if (String(it?.categoria || "") === "menores" && consumo.monto_presupuestado != null) {
    const disp = (Number(consumo.monto_presupuestado) || 0) - (Number(consumo.monto_comprado) || 0);
    return `${etiqueta}Fondo: ${_gs(consumo.monto_presupuestado)} · usado ${_gs(consumo.monto_comprado)} · disponible ${_gs(disp)}`;
  }
  return `${etiqueta}pide ${fmtCant(it?.quantity)} de ${fmtCant(consumo.presupuestado)} · comprado ${fmtCant(consumo.comprado)} (${consumo.pct_comprado}%)`;
};

export function datosPdfPedido(req, { ptoInfo = null, consumoMap = {}, consumoMatMap = null, nivelMap = {}, itemPtoMap = {} } = {}) {
  const presupuesto = presupuestoRef(req, ptoInfo);
  if (!req?.presupuesto_id) return { presupuesto, filas: null };
  const filas = agruparLineas(req.items).map((grupo) => {
    const item = grupo.base;
    const varias = grupo.lineas.length > 1;
    const consumo = !varias && item.item_uid ? consumoMap[item.item_uid] : null;
    const cm = _buscarMaterial(consumoMatMap, item);
    const nivelLabel = item.nivel_id ? (nivelMap[item.nivel_id] || null) : null;
    const ip = item.item_uid ? itemPtoMap[item.item_uid] : null;
    let itemTxt, nivelTxt, analisis;
    if (varias) {
      itemTxt = grupo.lineas.map(({ it }) => {
        const p = it.item_uid ? itemPtoMap[it.item_uid] : null;
        const nl = it.nivel_id ? (nivelMap[it.nivel_id] || "") : "";
        return `${fmtCant(it.quantity)}  ${p ? `${p.numero ? p.numero + " " : ""}${p.descripcion || ""}` : "sin imputar"}${nl ? ` [${nl}]` : ""}`;
      }).join("\n");
      nivelTxt = "según cada ítem";
      analisis = [cm ? _txtMaterial(cm) : null, ...grupo.lineas.map(({ it }) => _txtImputacion(
        it, it.item_uid ? consumoMap[it.item_uid] : null, it.item_uid ? itemPtoMap[it.item_uid] : null))]
        .filter(Boolean).join("\n");
    } else {
      itemTxt = ip ? `${ip.numero ? ip.numero + " " : ""}${ip.descripcion || ""}` : "sin imputar";
      nivelTxt = nivelLabel || "sin nivel";
      if (item.item_uid && consumo && String(item.categoria || "") === "menores" && consumo.monto_presupuestado != null) {
        analisis = _txtImputacion(item, consumo, ip);
      } else if (cm || consumo) {
        const delNivel = cm && nivelLabel && cm.por_nivel ? Number(cm.por_nivel[nivelLabel] || 0) : null;
        analisis = [
          cm ? _txtMaterial(cm) : null,
          consumo ? `En esta línea del Pto: ${fmtCant(consumo.presupuestado)} · comprado ${fmtCant(consumo.comprado)} (${consumo.pct_comprado}%)` : null,
          delNivel != null ? `En ${nivelLabel}: ${fmtCant(delNivel)} ${cm.unidad || ""}` : null,
        ].filter(Boolean).join("\n");
      } else {
        analisis = "sin imputar";
      }
    }
    if (item.tarea_numero) itemTxt += `\nTarea ${item.tarea_numero}`;
    return {
      categoria: PM_CATEGORIA_LABELS[item.categoria] || "—",
      descripcion: (item.material_name || "—") + (item.observacion ? `\n${item.observacion}` : ""),
      unidad: item.unit || "—",
      cantidad: String(fmtCant(grupo.cantidad)) + (varias ? `\n(en ${grupo.lineas.length} ítems)` : ""),
      item: itemTxt, nivel: nivelTxt, analisis,
      sector: item.sector || "—", frente: item.front || "—", marca: item.marca_recomendada || "—",
    };
  });
  return { presupuesto, filas };
}

// Desde los listados (sin el detalle abierto): trae los mismos datos y arma el PDF.
export async function descargarPedidoPDF(req) {
  if (!req?.presupuesto_id) { generatePedidoPDF(req, datosPdfPedido(req)); return; }
  const o = { withCredentials: true };
  const [ip, cons, mats] = await Promise.all([
    axios.get(`${API}/budgets/${req.presupuesto_id}/items-para-pedido`, o).then(r => r.data).catch(() => null),
    axios.get(`${API}/budgets/${req.presupuesto_id}/consumo`, o).then(r => r.data).catch(() => null),
    axios.get(`${API}/budgets/${req.presupuesto_id}/consumo-materiales`, o).then(r => r.data).catch(() => null),
  ]);
  const nivelMap = {};
  (ip?.niveles || []).forEach(n => { if (n?.id) nivelMap[n.id] = n.label || ""; });
  const itemPtoMap = {};
  (ip?.items || []).forEach(bi => { if (bi?.item_uid) itemPtoMap[bi.item_uid] = bi; });
  const consumoMap = {};
  (cons || []).forEach(row => { consumoMap[row.item_uid] = row; });
  generatePedidoPDF(req, datosPdfPedido(req, {
    ptoInfo: ip?.presupuesto || null, consumoMap, nivelMap, itemPtoMap,
    consumoMatMap: _indexarMateriales(mats?.materiales),
  }));
}

export function PedidoDetailModal({ open, onClose, request: req }) {
  // CEN 2.0 FASE 3 PARTE B — consumo por ítem del presupuesto (para que el
  // coordinador apruebe con contexto: cuánto ya se pidió/compró de cada ítem).
  const [consumoMap, setConsumoMap] = useState({});
  // Fix AS (2026-09-05): "no me sale ningun analisis al darle ver a ese pedido, me
  // tiene que salir el analisis, de eso depende mi aprobación" (Edson, 05/09). El
  // análisis por ítem solo existe si la línea fue imputada; se agrega el análisis
  // POR MATERIAL (el mismo que muestra la obra en Consumo de Materiales), que
  // sirve aunque la línea haya quedado como menor o imprevisto.
  const [consumoMatMap, setConsumoMatMap] = useState(null);   // Fix DC (2026-09-16): índice, no diccionario
  // Fix CW (2026-09-14): "necesitamos que se vean los tres tipos de análisis que había
  // mencionado anteriormente, también los ítems a los que serían imputados cada material
  // y los niveles, así como se ven cuando se están cargando para solicitar" (Edson,
  // 14/09). El nivel y el ítem viajan en la línea del pedido como id (`nivel_id`,
  // `item_uid`); los nombres viven en el presupuesto, así que se traen del mismo
  // endpoint que alimenta el formulario de carga.
  const [nivelMap, setNivelMap] = useState({});
  const [itemPtoMap, setItemPtoMap] = useState({});
  const [ptoInfo, setPtoInfo] = useState(null);   // Fix ED (2026-09-24)
  useEffect(() => {
    if (!open || !req?.presupuesto_id) { setConsumoMap({}); setConsumoMatMap(null); setNivelMap({}); setItemPtoMap({}); setPtoInfo(null); return; }
    axios.get(`${API}/budgets/${req.presupuesto_id}/items-para-pedido`, { withCredentials: true })
      .then(({ data }) => {
        setPtoInfo(data?.presupuesto || null);
        const nm = {};
        (data?.niveles || []).forEach(n => { if (n?.id) nm[n.id] = n.label || ""; });
        setNivelMap(nm);
        const im = {};
        (data?.items || []).forEach(bi => { if (bi?.item_uid) im[bi.item_uid] = bi; });
        setItemPtoMap(im);
      })
      .catch(() => {});
    axios.get(`${API}/budgets/${req.presupuesto_id}/consumo`, { withCredentials: true })
      .then(({ data }) => {
        const m = {};
        (data || []).forEach(row => { m[row.item_uid] = row; });
        setConsumoMap(m);
      })
      .catch(() => {});
    axios.get(`${API}/budgets/${req.presupuesto_id}/consumo-materiales`, { withCredentials: true })
      .then(({ data }) => setConsumoMatMap(_indexarMateriales(data?.materiales)))   // Fix DC (2026-09-16)
      .catch(() => {});
  }, [open, req?.presupuesto_id]);

  if (!open || !req) return null;
  const history = buildHistory(req);
  const HIST_COLORS = {
    pendiente: "bg-amber-100 text-amber-700 border-amber-200",
    aprobada: "bg-emerald-100 text-emerald-700 border-emerald-200",
    rechazada: "bg-red-100 text-red-700 border-red-200",
    modificar: "bg-orange-100 text-orange-700 border-orange-200",
  };
  return (
    <Modal open={open} onClose={onClose} title={`Pedido ${req.number}`} extraWide>
      <div className="space-y-5">
        <div className="bg-[#2E6DA4] rounded-xl p-5 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium opacity-70 uppercase tracking-wider">CEN INGENIERIA E.A.S.</p>
              <p className="text-xs opacity-60 mt-0.5">Instalaciones Eléctricas — Asunción, Paraguay</p>
            </div>
            <div className="text-right">
              <p className="text-xs opacity-70 uppercase">Pedido de Materiales</p>
              <p className="font-mono font-bold text-lg mt-0.5">{req.number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <StatusBadge status={req.status} />
            {req.urgency === "critica" && <span className="text-[10px] bg-red-400/30 text-red-100 px-2 py-0.5 rounded-full font-bold border border-red-300/30">CRÍTICO</span>}
            {req.urgency === "alta" && <span className="text-[10px] bg-orange-400/30 text-orange-100 px-2 py-0.5 rounded-full font-bold border border-orange-300/30">ALTA PRIORIDAD</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { label: "Proyecto / Obra", value: req.project_name },
            /* Fix Z (2026-09-02): orden de trabajo de asistencia, si el pedido se imputó a una. */
            ...(req.asistencia_number ? [{ label: "Orden de trabajo", value: req.asistencia_number }] : []),
            { label: "Solicitado por", value: req.requested_by_name },
            { label: "Fecha Solicitud", value: formatDateTime(req.created_at) },
            /* Fix L (2026-08-31): la fecha objetivo ahora viaja PM -> PP -> AP -> PF -> OC,
               así que este mismo bloque la muestra en el detalle de cualquiera de ellos.
               Los documentos creados antes del fix no la traen y muestran el guion. */
            { label: "Fecha Objetivo", value: req.target_date ? formatDate(req.target_date) : "—" },
            { label: "Urgencia", value: URGENCY_LABELS[req.urgency] || req.urgency || "—" },
            /* Fix ED (2026-09-24): presupuesto de referencia (código y nombre) y cliente. */
            { label: "Presupuesto de referencia", value: textoPresupuestoRef(presupuestoRef(req, ptoInfo)),
              extra: `Cliente: ${presupuestoRef(req, ptoInfo).cliente || "—"}`, testId: "pedido-detalle-presupuesto-ref" },
          ].map(({ label, value, extra, testId }) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-testid={testId}>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
              <p className="text-sm font-semibold text-gray-800 mt-1">{value}</p>
              {extra && <p className="text-xs font-semibold text-gray-600 mt-0.5">{extra}</p>}
            </div>
          ))}
        </div>
        {req.notes && (
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">Observaciones</p>
            <p className="text-sm text-blue-800 mt-1">{req.notes}</p>
          </div>
        )}

        <div>
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Materiales Solicitados</h4>
          {/* Fix AS (2026-09-05): por qué puede no haber análisis. */}
          {req.presupuesto_id && (req.items || []).some(it => !it.item_uid) && (
            <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-testid="pedido-detalle-aviso-sin-imputar">
              {(req.items || []).filter(it => !it.item_uid).length} ítem(s) sin imputar a una línea del presupuesto. Se puede corregir desde "Editar" antes de aprobar.
            </p>
          )}
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#2E6DA4] text-white">
                  {/* Fix CW (2026-09-14): el ítem del presupuesto y el nivel a los que se
                      imputa cada material, y el análisis completo de las tres líneas. */}
                  {["#", "Categ.", "Descripción", "UM", "Cant.", "Ítem del Pto.", "Nivel", "Análisis", "Sector", "Frente", "Marca Rec."].map(h => (
                    <th key={h} className="text-left px-3 py-2.5 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {agruparLineas(req.items).map((grupo, i) => {
                  const item = grupo.base;
                  const variasImputaciones = grupo.lineas.length > 1;
                  // El análisis "En esta línea del Pto" es de UNA línea del presupuesto:
                  // con varias imputaciones no hay una sola, así que se lista abajo.
                  const consumo = !variasImputaciones && item.item_uid ? consumoMap[item.item_uid] : null;
                  // Fix AS (2026-09-05): si la línea no está imputada, se busca el
                  // material en el listado desglosado del presupuesto.
                  const consumoMat = _buscarMaterial(consumoMatMap, item);   // Fix DC (2026-09-16)
                  // Fix CW (2026-09-14): nombre del nivel y del ítem del presupuesto de
                  // esta línea, y cuánto de este material le toca a ese nivel.
                  const nivelLabel = item.nivel_id ? (nivelMap[item.nivel_id] || null) : null;
                  const itemPto = item.item_uid ? itemPtoMap[item.item_uid] : null;
                  const delNivel = consumoMat && nivelLabel && consumoMat.por_nivel
                    ? Number(consumoMat.por_nivel[nivelLabel] || 0) : null;
                  return (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-blue-50/40"}>
                    <td className="px-3 py-2.5 text-[#2E6DA4] font-bold">{i + 1}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-[11px]">{PM_CATEGORIA_LABELS[item.categoria] || "—"}</td>
                    <td className="px-3 py-2.5 font-medium text-gray-800">
                      {item.material_name}
                      {/* Fix CW (2026-09-14): la observación por línea se guardaba desde el
                          Fix G pero no se mostraba en ninguna vista de lectura. */}
                      {item.observacion ? (
                        <span className="block text-[9px] font-normal text-gray-500 italic mt-0.5" data-testid={`pedido-detalle-observacion-${i}`}>{item.observacion}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-gray-500">{item.unit}</td>
                    <td className="px-3 py-2.5 font-bold text-gray-900">
                      {fmtCant(grupo.cantidad)}
                      {variasImputaciones && (
                        <span className="block text-[9px] font-normal text-gray-500" data-testid={`pedido-detalle-agrupado-${i}`}>
                          en {grupo.lineas.length} ítems
                        </span>
                      )}
                    </td>
                    {/* Fix CW (2026-09-14): a qué ítem del presupuesto se imputa. */}
                    <td className="px-3 py-2.5 text-[11px] text-gray-700 min-w-[150px]">
                      {variasImputaciones ? (
                        // Fix DJ (2026-09-19): el desglose del grupo. Una línea por ítem
                        // con la parte que le toca, para que se vea de un vistazo a dónde
                        // va la cantidad total de arriba sin tener seis filas iguales.
                        <div className="space-y-1" data-testid={`pedido-detalle-imputaciones-${i}`}>
                          {grupo.lineas.map(({ it, idx }) => {
                            const ip = it.item_uid ? itemPtoMap[it.item_uid] : null;
                            const nl = it.nivel_id ? (nivelMap[it.nivel_id] || null) : null;
                            return (
                              <div key={idx} className="flex items-start gap-1.5">
                                <span className="font-bold text-[#1A4B7A] flex-shrink-0 text-[10px] mt-px">{fmtCant(it.quantity)}</span>
                                <span className="text-gray-700">
                                  {ip ? (
                                    <>
                                      {ip.numero ? <span className="font-semibold text-[#2E6DA4]">{ip.numero} </span> : null}
                                      {ip.descripcion}
                                    </>
                                  ) : <span className="text-amber-600 font-semibold text-[10px]">sin imputar</span>}
                                  {nl ? <span className="ml-1 px-1 py-px rounded bg-[#EBF3FB] text-[#1A4B7A] text-[9px] font-bold">{nl}</span> : null}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : itemPto ? (
                        <span data-testid={`pedido-detalle-item-${i}`}>
                          {itemPto.numero ? <span className="font-semibold text-[#2E6DA4]">{itemPto.numero} </span> : null}
                          {itemPto.descripcion}
                          {itemPto.seccion ? <span className="block text-[9px] text-gray-400">{itemPto.seccion}</span> : null}
                        </span>
                      ) : req.presupuesto_id ? (
                        <span className="text-amber-600 text-[10px] font-semibold" data-testid={`pedido-detalle-item-sin-imputar-${i}`}>sin imputar</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                      {item.tarea_numero ? (
                        <span className="block text-[9px] text-gray-500 mt-0.5" data-testid={`pedido-detalle-tarea-${i}`}>Tarea {item.tarea_numero}</span>
                      ) : null}
                    </td>
                    {/* Fix CW (2026-09-14): a qué nivel se imputa. */}
                    {/* Fix DC (2026-09-16): sin nivel elegido no se puede mostrar el tercer
                        análisis (cuánto de este material le toca a ese nivel). El guion no
                        decía si faltaba el dato o si la pantalla estaba fallando. */}
                    <td className="px-3 py-2.5 text-[11px] text-gray-700" data-testid={`pedido-detalle-nivel-${i}`}>
                      {variasImputaciones
                        ? <span className="text-gray-400 text-[10px]">según cada ítem</span>
                        : nivelLabel
                          ? nivelLabel
                          : req.presupuesto_id
                            ? <span className="text-amber-600 text-[10px] font-semibold">sin nivel</span>
                            : <span className="text-gray-300">—</span>}
                    </td>
                    {/* Fix CW (2026-09-14): los mismos tres análisis que muestra el
                        formulario de carga — el material en todo el presupuesto (el que
                        manda), esta línea del presupuesto, y el nivel de esta fila. */}
                    <td className="px-3 py-2.5 text-[11px] min-w-[200px]">
                      {variasImputaciones ? (
                        // Fix DK (2026-09-21): "cuando hay varios items seleccionados en un
                        // material solicitado ya no aparecen los analisis (…) Deberia aparecer
                        // para cada item el resultado de la division del total solicitado que
                        // le corresponde a cada item, cruzado con la cantidad proyectada de
                        // cada item". El Fix DJ ponía "sin imputar" acá aunque las líneas SÍ
                        // estaban imputadas. Ahora: el análisis del material una vez, y debajo
                        // una línea por imputación con la parte que le toca contra lo proyectado.
                        <div className="space-y-0.5" data-testid={`pedido-detalle-analisis-multi-${i}`}>
                          {consumoMat && (
                            <p className={`text-[10px] font-semibold ${CONSUMO_ESTADO_CLS[consumoMat.estado] || "text-gray-500"}`}>
                              Material en el Pto: {fmtCant(consumoMat.presupuestado)} {consumoMat.unidad} · pedido {fmtCant(consumoMat.solicitado)} · comprado {fmtCant(consumoMat.comprado)} ({consumoMat.pct_comprado}%)
                            </p>
                          )}
                          {grupo.lineas.map(({ it, idx }) => (
                            <p key={idx} className="leading-tight">
                              {analisisImputacion({
                                it, idx,
                                consumo: it.item_uid ? consumoMap[it.item_uid] : null,
                                ip: it.item_uid ? itemPtoMap[it.item_uid] : null,
                              })}
                            </p>
                          ))}
                        </div>
                      ) : item.item_uid && consumo && String(item.categoria || "") === "menores" && consumo.monto_presupuestado != null ? (
                        // Fix DK (2026-09-21): material menor imputado a un solo ítem → fondo en guaraníes.
                        <div className="space-y-0.5">
                          {analisisImputacion({ it: item, idx: i, consumo, ip: itemPto })}
                        </div>
                      ) : consumoMat || consumo ? (
                        <div className="space-y-0.5">
                          {consumoMat && (
                            <p className={`text-[10px] font-semibold ${CONSUMO_ESTADO_CLS[consumoMat.estado] || "text-gray-500"}`} data-testid={`pedido-detalle-consumo-material-${i}`}>
                              Material en el Pto: {fmtCant(consumoMat.presupuestado)} {consumoMat.unidad} · pedido {fmtCant(consumoMat.solicitado)} · comprado {fmtCant(consumoMat.comprado)} ({consumoMat.pct_comprado}%)
                            </p>
                          )}
                          {consumo && (
                            <p className="text-[10px] text-gray-500" data-testid={`pedido-detalle-consumo-${i}`}>
                              En esta línea del Pto: {fmtCant(consumo.presupuestado)} · comprado {fmtCant(consumo.comprado)} ({consumo.pct_comprado}%)
                            </p>
                          )}
                          {delNivel != null && (
                            <p className="text-[10px] text-gray-500" data-testid={`pedido-detalle-consumo-nivel-${i}`}>
                              En {nivelLabel}: {fmtCant(delNivel)} {consumoMat.unidad}
                            </p>
                          )}
                        </div>
                      ) : req.presupuesto_id ? (
                        <span className="text-amber-600 text-[10px] font-semibold" data-testid={`pedido-detalle-sin-imputar-${i}`}>sin imputar</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-500">{item.sector || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500">{item.front || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 italic">{item.marca_recomendada || "—"}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <History size={13} className="text-gray-500" />
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Historial del Pedido</h4>
          </div>
          {history.length === 0 ? (
            <p className="text-xs text-gray-400 italic">Sin historial registrado</p>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold border ${HIST_COLORS[h.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                      {i + 1}
                    </div>
                    {i < history.length - 1 && <div className="w-px h-full min-h-[16px] bg-gray-200 mt-1" />}
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800">{h.action}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${HIST_COLORS[h.status] || "bg-gray-100 text-gray-500 border-gray-200"}`}>
                        {STATUS_LABELS[h.status] || h.status}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      <span className="font-medium text-gray-600">{h.by}</span> · {formatDateTime(h.at)}
                    </p>
                    {h.comment && (
                      <p className="text-[11px] text-gray-500 mt-1 italic bg-gray-50 rounded-lg px-2 py-1 border border-gray-100">"{h.comment}"</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button onClick={() => generatePedidoPDF(req, datosPdfPedido(req, { ptoInfo, consumoMap, consumoMatMap, nivelMap, itemPtoMap }))}
            className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-xs font-semibold transition-all"
            data-testid="pdf-from-modal-btn">
            <Download size={14} /> Descargar PDF
          </button>
          <button onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-xs font-medium">
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Fix O (2026-08-31): historial del pedido de origen, para mostrarlo en cada paso
// del flujo. Pedido textual de Sergio el 24/08 ("si se puede hacer repetir no más
// ya hasta el final del proceso") y repetido en el documento del 29/08. Se apoya en
// pm_origen_id, que desde este fix viaja PM -> PP -> AP -> PF -> OC. Si el documento
// no lo trae (los anteriores a este fix), la tarjeta no se dibuja y no molesta.
export function HistorialPedidoCard({ pmId }) {
  const [pm, setPm] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!pmId) { setPm(null); return; }
    setError(false);
    axios.get(`${API}/purchases/pedido-origen/${pmId}`, { withCredentials: true })
      .then(({ data }) => setPm(data))
      .catch(() => setError(true));
  }, [pmId]);
  if (!pmId || error) return null;
  if (!pm) return <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 text-xs text-gray-400">Cargando el pedido de origen…</div>;
  const history = pm.history || [];
  return (
    <div className="bg-blue-50/40 rounded-xl p-3.5 border border-blue-100" data-testid="historial-pedido-origen">
      <div className="flex items-center gap-2 mb-2.5">
        <History size={13} className="text-[#2E6DA4]" />
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Pedido de origen · {pm.number}</h4>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {[
          ["Solicitado por", pm.requested_by_name],
          ["Fecha de solicitud", pm.created_at ? formatDateTime(pm.created_at) : "—"],
          ["Fecha objetivo", pm.target_date ? formatDate(pm.target_date) : "—"],
          ["Obra", pm.lista_obra_nombre],
        ].map(([l, v]) => (
          <div key={l}>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{l}</p>
            <p className="text-xs font-semibold text-gray-800 mt-0.5">{v || "—"}</p>
          </div>
        ))}
      </div>
      {pm.notes && <p className="text-[11px] text-gray-500 italic mb-2.5">"{pm.notes}"</p>}
      {history.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Sin historial registrado</p>
      ) : (
        <div className="space-y-1.5">
          {history.map((h, i) => (
            <div key={i} className="flex items-start gap-2 text-[11px]">
              <span className="w-5 h-5 rounded-full bg-white border border-blue-200 text-[9px] font-bold text-[#2E6DA4] flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
              <div className="min-w-0">
                <p className="text-gray-700 font-medium">{h.action || h.event || "—"}</p>
                <p className="text-gray-400">{h.by_name || h.user_name || ""}{h.at || h.date ? ` · ${formatDateTime(h.at || h.date)}` : ""}</p>
                {h.comment && <p className="text-gray-500 italic mt-0.5">"{h.comment}"</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
